import { call, put, select, takeEvery } from 'redux-saga/effects'

import {
  agentSourcedCreatorActionFailure,
  agentSourcedCreatorActionSuccess,
  undoAgentSourcedCreatorAction,
} from '@store/agentSourcedCreators/actions'
import { selectAgentSourcedCreators } from '@store/agentSourcedCreators/selectors'
import { selectAuthToken } from '@store/auth/selectors'
import { extractVerbatimErrorMessage } from '@store/discoveryRuns/api'
import { toast } from '@tribegroup'
import { networkRequest } from '@utils/networkRequest'

const creatorDisplayName = (creators: ReadonlyArray<any>, id: number): string => {
  const c = creators.find((x) => x.id === id)
  return (
    c?.creator_data?.fullname ?? c?.creator_data?.handle ?? c?.creator_data?.username ?? 'Creator'
  )
}

// BRAND-9582 item 15: the BE collapses two distinct guards (stale/
// not-decidable run, already-sent intent) into this one error_code at HTTP
// 422 — both mean "undo is no longer available", so both get the same copy.
const UNDO_NOT_AVAILABLE_ERROR_CODE = 'agent_sourced_creators.undo.validation_error'
const UNDO_NOT_AVAILABLE_MESSAGE = 'Undo is no longer available for this creator'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isUndoNotAvailableError = (error: any): boolean => {
  const messages = error?.messages
  if (!Array.isArray(messages)) return false
  return messages.some((m: any) => m?.error_code === UNDO_NOT_AVAILABLE_ERROR_CODE) // eslint-disable-line @typescript-eslint/no-explicit-any
}

// Exported for direct unit testing, matching unshortlistCreator/shortlistCreator.
export function* undoCreatorActionSaga(action: ReturnType<typeof undoAgentSourcedCreatorAction>) {
  try {
    const { creatorId, href, method } = action.payload
    const token: string = yield select(selectAuthToken)
    const response: { id: number; status: string; links: unknown } = yield call(
      networkRequest,
      method,
      href,
      undefined,
      token,
    )
    yield put(
      agentSourcedCreatorActionSuccess({
        creatorId,
        status: 'pending',
        updatedLinks: response.links as any,
      }),
    )
    const creators: ReadonlyArray<any> = yield select(selectAgentSourcedCreators)
    yield call(toast, `Undone for ${creatorDisplayName(creators, creatorId)}`)
  } catch (e: any) {
    const notAvailable = isUndoNotAvailableError(e)
    const message = notAvailable ? UNDO_NOT_AVAILABLE_MESSAGE : extractVerbatimErrorMessage(e)
    yield put(
      agentSourcedCreatorActionFailure({
        creatorId: action.payload.creatorId,
        error: message,
      }),
    )
    // Ted's copy already reads as a complete sentence — skip the "Undo
    // failed: " prefix for it; keep the prefix for the generic/verbatim path.
    yield call(toast, notAvailable ? message : `Undo failed: ${message}`)
  }
}

export default function* watchUndoAgentSourcedCreatorAction() {
  yield takeEvery(undoAgentSourcedCreatorAction.type, undoCreatorActionSaga)
}
